import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Sentinel step for the post-submit confirmation screen. Numeric steps are
 * 1..totalSteps; this sits after them.
 */
export const DONE_STEP = "done";

/**
 * Keeps the current step in the URL (`?step=3`, or `?step=done` for the
 * confirmation) so browser back/forward move between steps instead of leaving
 * the flow.
 *
 * A search param rather than a path segment: the three flows already own a
 * route each, and their steps differ in count and meaning, so `?step=` avoids
 * declaring a dozen extra routes for what is one linear wizard.
 *
 * Each change pushes a history entry — that's what makes Back work. Step 1
 * stays the bare URL so it matches the link in the outreach email, which also
 * means Back from step 1 leaves the flow to the landing page.
 *
 * Out-of-range or non-numeric values clamp to step 1, so a hand-edited or
 * stale URL can't render a blank screen.
 */
export function useStepParam(totalSteps: number): {
  /** Current numeric step (1-based). Meaningless when `done` is true. */
  step: number;
  /** True when the confirmation screen should be shown. */
  done: boolean;
  goTo: (next: number) => void;
  finish: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawParam = searchParams.get("step");
  const done = rawParam === DONE_STEP;

  const raw = Number(rawParam);
  const step =
    Number.isFinite(raw) && raw >= 1 && raw <= totalSteps ? Math.floor(raw) : 1;

  const write = useCallback(
    (value: string | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value === null) params.delete("step");
          else params.set("step", value);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.min(Math.max(next, 1), totalSteps);
      write(clamped === 1 ? null : String(clamped));
    },
    [write, totalSteps],
  );

  const finish = useCallback(() => write(DONE_STEP), [write]);

  return { step, done, goTo, finish };
}
